import AuthServices from "../services/AuthServices";
import BusinessService from "../../dashboard/profile/pages/business/services/index";
import dashboardService from "../../dashboard/reports/services/dashboardService";
import router from "@/router";

// Secretos del negocio que versiones anteriores del backend mandaban en el
// perfil (contraseña y ruta del .p12, token de tarjeta). El backend ya los
// oculta; esto los borra de perfiles viejos guardados en localStorage.
const BUSINESS_SECRET_KEYS = [
  "business_sri_password",
  "business_sri_file",
  "business_signing_password",
  "document_signing_password",
  "pp_card_token"
];

function stripBusinessSecrets(user) {
  if (!user || typeof user !== "object") return user;
  const clean = business => {
    if (!business || typeof business !== "object") return business;
    const copy = { ...business };
    BUSINESS_SECRET_KEYS.forEach(key => delete copy[key]);
    return copy;
  };
  const result = { ...user };
  if (result.user_business) result.user_business = clean(result.user_business);
  if (Array.isArray(result.businesses)) {
    result.businesses = result.businesses.map(clean);
  }
  return result;
}

// Cargar usuario de localStorage, pero validar que el token sea un JWT válido.
// Si el token es inválido (corrupto), tratar como usuario no autenticado.
function loadUserFromStorage() {
  try {
    const stored = JSON.parse(localStorage.getItem("user")) || {};
    if (stored.token && stored.token.split(".").length !== 3) {
      // Token inválido: limpiar y devolver vacío
      localStorage.removeItem("user");
      localStorage.removeItem("last_activity");
      return {};
    }
    const clean = stripBusinessSecrets(stored);
    if (JSON.stringify(clean) !== JSON.stringify(stored)) {
      localStorage.setItem("user", JSON.stringify(clean));
    }
    return clean;
  } catch (e) {
    localStorage.removeItem("user");
    return {};
  }
}

export default {
  state: {
    user: loadUserFromStorage()
  },

  mutations: {
    setUser: (state, user) => {
      state.user = stripBusinessSecrets(user);
      localStorage.setItem("user", JSON.stringify(state.user));
    },

    setLogout: state => {
      state.user = {};
    }
  },

  actions: {
    validateEmailExist: ({ dispatch }, data) => {
      dispatch("toggleRequestLoading");
      return new Promise((resolve, reject) => {
        AuthServices.validateEmailExist(data)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          })
          .finally(() => dispatch("toggleRequestLoading"));
      });
    },
    register: ({ commit, dispatch }, data) => {
      dispatch("toggleRequestLoading");
      return new Promise((resolve, reject) => {
        AuthServices.register(data)
          .then(response => {
            if (response.data.success) {
              const { user, token, extras } = response.data;
              commit("setUser", { ...user, token, extras });

              resolve(true);
            }
            reject(false);
          })
          .catch(error => {
            reject(error);
          })
          .finally(() => dispatch("toggleRequestLoading"));
      });
    },
    login: ({ commit, dispatch }, data) => {
      dispatch("toggleRequestLoading");
      return new Promise((resolve, reject) => {
        AuthServices.login(data)
          .then(response => {
            if (response.data.success) {
              const { user, token } = response.data;
              commit("setUser", { ...user, token });
              // Fetch login context asynchronously after auth — does not block redirect
              AuthServices.getLoginContext()
                .then(ctx => {
                  if (ctx && ctx.data && ctx.data.success) {
                    dispatch(
                      "notificationsStore/setLoginContext",
                      ctx.data.data,
                      {
                        root: true
                      }
                    );
                  }
                })
                .catch(() => {});
            }
            resolve(response);
          })
          .catch(error => {
            localStorage.removeItem("user");
            reject(error);
          })
          .finally(() => dispatch("toggleRequestLoading"));
      });
    },
    logout({ commit, dispatch }) {
      return new Promise(resolve => {
        dispatch("clearPermissions");
        commit("setLogout");
        localStorage.removeItem("user");
        resolve();
      });
    },
    profileUpdate({ state, commit, dispatch }, data) {
      dispatch("toggleRequestLoading");
      return new Promise((resolve, reject) => {
        AuthServices.profileUpdate(data)
          .then(response => {
            if (response.data.success) {
              const { user } = response.data;
              commit("setUser", { ...state.user, ...user });
              resolve(response);
            }
            reject(false);
          })
          .catch(error => reject(error))
          .finally(() => dispatch("toggleRequestLoading"));
      });
    },
    userUpdate({ state, commit }, data) {
      commit("setUser", { ...state.user, ...data });
    },
    userBusinessUpdated({ dispatch, commit, getters }, user_business) {
      dispatch("toggleRequestLoading");
      return new Promise((resolve, reject) => {
        BusinessService.selectBusiness(
          user_business.business_ruc,
          // Negocio de la lista (id = negocio) o membresía (business_id).
          user_business.business_id || user_business.id
        )
          .then(response => {
            const { data } = response;
            const { success, data: business } = data;
            if (success) {
              const user = {
                ...getters.getUser,
                ...{ user_business: business }
              };
              commit("setUser", user);
              // Multi-usuario: el rol y los permisos son por negocio.
              dispatch("clearPermissions");
              dispatch("loadMyPermissions");
              // Vacía caches del dashboard que estaban keyed solo por
              // `period` — sin esto el frontend muestra métricas del
              // negocio anterior tras hacer el switch.
              dashboardService.clearAll();
              // Notifica a componentes con caches internas (DashboardKpis,
              // RecentQuotes, etc.) que deben re-fetchear desde cero.
              this._vm.$root.$emit("dashboard-kpis-refresh");
              this._vm.$root.$emit("business-changed", business.id);

              // Redirige al dashboard del nuevo negocio. Evita el escenario
              // donde el usuario estaba viendo /factura/123 de Negocio A y
              // al cambiar a Negocio B esa factura no existe → 404.
              // Skip si ya estamos en el dashboard (no causar navegación inútil).
              const currentRouteName =
                router.currentRoute && router.currentRoute.name;
              if (currentRouteName !== "dashboard") {
                router.push({ name: "dashboard" }).catch(() => {
                  // Ignorar NavigationDuplicated u otros errores benignos
                });
              }
              resolve(response);
            }
            reject(false);
          })
          .catch(error => reject(error))
          .finally(() => dispatch("toggleRequestLoading"));
      });
    },
    userPlanUpdated({ commit, getters }, plan) {
      const user = {
        ...getters.getUser,
        plan
      };
      commit("setUser", user);
    },
    userConfigGeneralUpdated({ commit, getters }, userConfigGeneral) {
      const { user_config_general } = getters.getUser;
      const setUserConfigGeneral = {
        ...user_config_general,
        ...userConfigGeneral
      };

      commit("setUser", {
        ...getters.getUser,
        user_config_general: setUserConfigGeneral
      });

      AuthServices.updatedUserConfigGeneral(setUserConfigGeneral);
    }
  },

  getters: {
    getUser: ({ user }) => user,
    isLogged: ({ user }) => !!user.token,
    userInfoCompleted: ({ user }) => {
      const { email, last_name, name, phone } = user;
      if (email && last_name && name && phone) return true;
      return false;
    },
    userInfoBusinessCompleted: ({ user }) => {
      const {
        business_address,
        business_email,
        business_logo,
        business_name,
        business_phone,
        business_ruc,
        category_id,
        city_id,
        people_number
      } = user.user_business;
      if (
        business_address &&
        business_email &&
        business_logo &&
        business_name &&
        business_phone &&
        business_ruc &&
        category_id &&
        city_id &&
        people_number
      )
        return true;
      return false;
    },
    isPlanSubscriptions({ user }) {
      const { user_business } = user || {};
      return user_business ? user_business.is_plan_subscriptions : false;
    }
  }
};
